import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ColumnApi, GridApi, GridOptions, IDatasource, IGetRowsParams, RowNodeTransaction } from 'ag-grid-community';
import { Workbook } from 'exceljs';
import * as fs from 'file-saver';
import { Subscription, fromEvent, of } from 'rxjs';
import { AddRemoveColumnSelectorComponent } from 'src/app/components/add-remove-column-selector/add-remove-column-selector.component';
import { StatusPopoverComponent } from 'src/app/components/status-popover/status-popover.component';
import { popoverMenu } from 'src/app/modals/popover-menu';
import { LocalSettingsService } from 'src/app/services/local-settings/local-settings.service';
import { isNotEmptyArray } from 'src/app/utilities/utils';
import { GridConfig } from '../../grid-config';
import { GridActionFocusRestore } from '../grid-action-focus-restore';
import { ActionsRendererComponent } from '../controls/actions-renderer/actions-renderer.component';
import { ButtonsRendererComponent } from '../controls/buttons-renderer/buttons-renderer.component';
import { FieldValueRendererComponent } from '../controls/field-value-renderer/field-value-renderer.component';
import { ProgressBarRendererComponent } from '../controls/progress-bar-renderer/progress-bar-renderer.component';
import { DateRendererComponent } from '../controls/date-renderer/date-renderer.component';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { CheckBoxRendererComponent } from '../controls/checkbox-renderer/checkbox-renderer.component';
import { UserNameRendererComponent } from '../controls/user-name-renderer/user-name-renderer.components';
import { CountryFlagRendererComponent } from '../controls/country-flag-renderer/country-flag-renderer.components';
import { HoverDataRendererComponent } from '../controls/hover-data-renderer/hover-data-renderer.component';
import { HoverRoleRendererComponent } from '../controls/hover-role-renderer/hover-role-renderer.component';
import { RadioRendererComponent } from '../controls/radio-renderer/radio-renderer.component';
import { LockStatusRendererComponent } from '../controls/lock-status-renderer/lock-status-renderer.component';
import { ControlledTextFloatingFilterComponent } from '../controls/controlled-text-floating-filter/controlled-text-floating-filter.component';


@Component({
  selector: 'app-grid',
  templateUrl: './grid-component.html',
  styleUrls: ['./grid-component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppGridComponent implements OnInit, OnDestroy, AfterViewInit {
  private static isKeyboardNavigation = true;
  private readonly agGridLocaleKeyMap: Record<string, string[]> = {
    contains: ['Contains'],
    notContains: ['Does Not Contain', 'Not Contains'],
    equals: ['Equals'],
    notEqual: ['Not Equal'],
    startsWith: ['Starts with'],
    endsWith: ['Ends with'],
    blank: ['Blank'],
    notBlank: ['Not Blank'],
    applyFilter: ['Apply', 'apply'],
    resetFilter: ['Reset', 'Clear'],
    clearFilter: ['Clear'],
    filterOoo: ['Filter'],
    searchOoo: ['Search'],
  };

  @ViewChild('agGrid', { read: ElementRef }) agGridElement: ElementRef<HTMLElement>;
  @ViewChild('gridMoreIcon', { read: ElementRef }) gridMoreIcon: ElementRef<HTMLElement>;
  @Input('parentComponentName')
  parentComponent : string = '';

  @Input('columns')
  columnDefs: any[];

  @Input('data')
  allGridData: any;

  @Input('gridConfigItems')
  gridConfigItems: GridConfig;

  @Input('showKebabIcon')
  showKebabIcon = true;

  @Input('showHeader')
  showHeader = true;
  @Input() skipHeaderOnTab = false;
  @Input() bodyOnlyTabNavigation = false;
  @Input() skipLeadingEmptyHeaderOnTab = false;
  @Input() enableBodyCellKeyboardNavigation?: boolean;
  @Input() enableCheckboxEnterToggle = false;
  @Input() isVisible = true;
  @Input() resetScrollOnVisible = false;
  @Input() searchDisabled = false;
  @Input() onLoadMore: (offset: number, searchText: string, callback?: () => void) => void;
  @Input() loadMorePageSize = 10;

  currentOffset: number = 0;
  hasMoreItems: boolean = true;
  isLoadingMore: boolean = false; // In-flight guard for infinite scroll load-more
  private readonly viewportAutoFillParents = new Set(['LibraryManagementInitialHazards', 'LibraryManagementMeasures', 'CloneRoadmap']);
  debounceTimeout: any; // To store the timeout ID for debounce
  searchDebounceTimeout: any; // To store the timeout ID for search debounce
  filterModifiedDebounceTimeout: any; // To store the timeout ID for filter debounce
  private visibleGridRefreshTimeout: any;
  private scrollResetTimeout: any;
  private viewportFillTimeout: any;
  lastSearchText: string = ''; // To track the last search text to prevent duplicate calls
  private isComponentDestroyed = false;
  private isRestoringFilterState = false;

  @Output()
  onCellClick = new EventEmitter<any>();

  @Output()
  onFilterChanged = new EventEmitter<any>();

  @Output()
  onCheckAll = new EventEmitter<boolean>();

  @Output()
  onBtnClick = new EventEmitter<any>();

  gridData: any;
  gridOptions: GridOptions;
  gridApi: GridApi;
  gridColumnApi: ColumnApi;
  gridstyle: any;
  gridWrapperstyle: any = {
    width: '100%',
    height: '100%',
  };
  rowClassRules;
  gridlastRowIndex: number;
  private gridSortColumn: string;
  private gridSortOrder: string;
  private group_MIN_HEIGHT = 40;
  private header_MIN_HEIGHT = 40;
  private isAllChecked = false;
  private overlayMutationObserver?: MutationObserver;
  private focusedCellTooltipTarget?: HTMLElement;
  private isDispatchingTooltipFocus = false;
  private tooltipShowTimeout?: ReturnType<typeof setTimeout>;
  public gridFocusKey = '';
  private readonly onWindowFocusRestore = () => this.handleReturnFocusRestore(true);
  private readonly onPageShowRestore = () => this.handleReturnFocusRestore(true);
  private readonly onGridBodyScroll = (event?: any) => this.debounceScroll(event);
  private readonly onGridModelUpdated = () => this.handleReturnFocusRestore(false);
  @Input('searchText')
  searchText: string ='';
  filters: any = null;
  param: string;
  resizeSubscription$: Subscription;
  languageChangeSubscription$: Subscription;

  overlayLoadingTemplate: string;
  overlayNoRowsTemplate: string;
  isDataLoading = false;
  public loading= true;
  public shimmerGrid: number[] = Array(56).fill(0); // 8 rows * 7 columns = 56 cells
  isKeyboardFocusVisible = false;
  isRefresh = false;
  autoGroupColumnDef: any;
  rowGroupPanelShow: any;
  pinnedBottomRowData: any;
  
  @Output() searchTextChanged: EventEmitter<string> = new EventEmitter<string>();
  constructor(private translate: TranslateService,
    public popoverController: PopoverController,
    public modalController: ModalController,
    public sharedValue: ShareValuesService,
    public localSettingsSrv: LocalSettingsService) {
    this.overlayNoRowsTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative">
        ${this.translate.instant('no_data_availabe')}
      </div>
    `;
    this.overlayLoadingTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative">
        ${this.translate.instant('data_loading')}
      </div>
    `;
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes?.searchText) {
      this.searchText = changes?.searchText?.currentValue;
    }

    if (changes?.columnDefs && !changes.columnDefs.firstChange && this.gridApi) {
      this.updateGridColumnDefinitions(changes.columnDefs.currentValue);
    }
    if (changes?.isVisible && !changes.isVisible.firstChange) {
      if (changes.isVisible.currentValue) {
        this.scheduleVisibleGridRefresh();
        if (this.resetScrollOnVisible) {
          this.scheduleScrollReset();
        }
      } else {
        this.releaseGridFocus();
        this.clearPendingActionFocusForThisGrid();
      }
    }
  }

  ngOnInit() {
    const resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = resizeObservable$.subscribe(evt => {
      this.setWidthAndHeight();
    });
    this.languageChangeSubscription$ = this.translate.onLangChange.subscribe(() => {
      this.overlayNoRowsTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative">
        ${this.translate.instant('no_data_availabe')}
      </div>
    `;
      this.overlayLoadingTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative">
        ${this.translate.instant('data_loading')}
      </div>
    `;

      if (this.gridApi) {
        this.gridOptions = {
          ...this.gridOptions,
          localeText: this.buildAgGridLocaleText(),
        };
        this.gridApi.refreshHeader();
      }
    });
    this.sharedValue.isGridDataLoading = true;
  }
  ngAfterViewInit() {
    this.startOverlayMutationObserver();
    window.addEventListener('focus', this.onWindowFocusRestore);
    window.addEventListener('pageshow', this.onPageShowRestore);
  }

  ngOnDestroy() {
    this.isComponentDestroyed = true;
    window.removeEventListener('focus', this.onWindowFocusRestore);
    window.removeEventListener('pageshow', this.onPageShowRestore);
    this.clearFocusedCellTooltip();
    if (this.tooltipShowTimeout) {
      clearTimeout(this.tooltipShowTimeout);
      this.tooltipShowTimeout = undefined;
    }

    if (this.overlayMutationObserver) {
      this.overlayMutationObserver.disconnect();
    }

    if (this.resizeSubscription$) {
      this.resizeSubscription$.unsubscribe();
    }

    if (this.languageChangeSubscription$) {
      this.languageChangeSubscription$.unsubscribe();
    }

    this.releaseGridFocus();
    GridActionFocusRestore.clear();

    if (this.gridApi && !this.isGridDestroyed()) {
      this.gridApi.removeEventListener('bodyScroll', this.onGridBodyScroll);
      this.gridApi.removeEventListener('modelUpdated', this.onGridModelUpdated);
    }
    // Clean up debounce timeout
    clearTimeout(this.debounceTimeout);
    clearTimeout(this.filterModifiedDebounceTimeout);
    clearTimeout(this.visibleGridRefreshTimeout);
    clearTimeout(this.scrollResetTimeout);
  }

  private scheduleVisibleGridRefresh(): void {
    clearTimeout(this.visibleGridRefreshTimeout);
    this.visibleGridRefreshTimeout = setTimeout(() => {
      requestAnimationFrame(() => this.refreshVisibleGridLayout());
    }, 0);
  }

  private refreshVisibleGridLayout(): void {
    if (!this.gridApi || this.isGridDestroyed() || !this.agGridElement?.nativeElement || !this.isVisible) {
      return;
    }

    this.gridApi.resetRowHeights();
    this.gridApi.refreshCells({ force: true, suppressFlash: true });
    this.gridApi.redrawRows();
  }

  private getTranslatedAgGridLabel(key: string, defaultValue: string): string {
    const translationKeys = this.agGridLocaleKeyMap[key] || [];

    for (const translationKey of translationKeys) {
      const translatedLabel = this.translate.instant(translationKey);

      if (translatedLabel) {
        if (key === 'filterOoo' || key === 'searchOoo') {
          return translatedLabel.endsWith('...') ? translatedLabel : `${translatedLabel}...`;
        }

        return translatedLabel;
      }
    }

    return defaultValue;
  }

  private buildAgGridLocaleText(): Record<string, string> {
    return Object.keys(this.agGridLocaleKeyMap).reduce((localeText, key) => {
      localeText[key] = this.getTranslatedAgGridLabel(key, key);
      return localeText;
    }, {} as Record<string, string>);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    if (!['Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      return;
    }

    AppGridComponent.isKeyboardNavigation = true;

    if (this.isGridFocusWithin()) {
      this.isKeyboardFocusVisible = true;
    }
  }

  @HostListener('document:mousedown')
  @HostListener('document:touchstart')
  onDocumentPointerInteraction() {
    AppGridComponent.isKeyboardNavigation = false;
    this.isKeyboardFocusVisible = false;
  }

  private startOverlayMutationObserver(): void {
    if (!document?.body) {
      return;
    }

    this.overlayMutationObserver = new MutationObserver(() => this.handleReturnFocusRestore(false));
    this.overlayMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private clearFocusedCellTooltip(): void {
    if (!this.focusedCellTooltipTarget) {
      this.removeAllRenderedTooltips();
      return;
    }

    this.focusedCellTooltipTarget.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
    this.focusedCellTooltipTarget.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
    this.focusedCellTooltipTarget.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
    this.focusedCellTooltipTarget.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
    this.focusedCellTooltipTarget = undefined;
    this.removeAllRenderedTooltips();
  }

  private removeAllRenderedTooltips(): void {
    const renderedTooltips = Array.from(document.querySelectorAll('.ng-tooltip')) as HTMLElement[];
    renderedTooltips.forEach((tooltip) => {
      tooltip.parentElement?.removeChild(tooltip);
    });
  }

  private showTooltipForFocusedCell(rowIndex: number, colId: string, preferredTarget?: HTMLElement): void {
    if (!this.agGridElement?.nativeElement) {
      return;
    }

    let focusCell: HTMLElement | undefined;
    if (preferredTarget) {
      focusCell = preferredTarget.closest('.ag-cell') as HTMLElement | undefined;
    }

    if (!focusCell && rowIndex != null && colId) {
      const rowSelector = `.ag-row[row-index="${rowIndex}"]`;
      const cellSelector = `.ag-cell[col-id="${colId}"]`;
      const rowElements = Array.from(this.agGridElement.nativeElement.querySelectorAll(rowSelector)) as HTMLElement[];
      focusCell = rowElements
        .map((rowElement) => rowElement.querySelector(cellSelector) as HTMLElement)
        .find((cell) => !!cell);
    }

    if (!focusCell) {
      return;
    }

    const activeElement = document.activeElement as HTMLElement | null;
    const resolvedPreferredTarget = preferredTarget && focusCell.contains(preferredTarget)
      ? preferredTarget
      : (activeElement && focusCell.contains(activeElement) ? activeElement : undefined);

    if (!this.usesDirectiveTooltip(colId)) {
      this.showAgGridTooltipForCell(focusCell);
      return;
    }

    const candidates = this.resolveTooltipCandidates(resolvedPreferredTarget, focusCell, colId);

    for (const candidate of candidates) {
      this.removeAllRenderedTooltips();
      this.isDispatchingTooltipFocus = true;
      candidate.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
      candidate.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
      candidate.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
      this.isDispatchingTooltipFocus = false;

      if (document.querySelector('.ng-tooltip')) {
        this.focusedCellTooltipTarget = candidate;
        return;
      }
    }

    this.isDispatchingTooltipFocus = false;
  }

  private usesDirectiveTooltip(colId: string): boolean {
    const rendererName = this.gridColumnApi?.getColumn(colId)?.getColDef?.()?.cellRenderer;
    return rendererName === 'actionRenderer' || rendererName === 'lockStatusRenderer';
  }

  private showAgGridTooltipForCell(focusCell: HTMLElement): void {
    this.removeAllRenderedTooltips();

    const rect = focusCell.getBoundingClientRect();
    const clientX = rect.left + Math.min(Math.max(rect.width / 2, 8), Math.max(rect.width - 8, 8));
    const clientY = rect.top + Math.min(Math.max(rect.height / 2, 8), Math.max(rect.height - 8, 8));
    const mouseEventInit: MouseEventInit = {
      bubbles: true,
      cancelable: true,
      clientX,
      clientY,
      view: window,
    };

    focusCell.dispatchEvent(new MouseEvent('mouseenter', mouseEventInit));
    focusCell.dispatchEvent(new MouseEvent('mouseover', mouseEventInit));
    focusCell.dispatchEvent(new MouseEvent('mousemove', mouseEventInit));
    this.focusedCellTooltipTarget = focusCell;
  }

  private resolveTooltipCandidates(preferredTarget?: HTMLElement, focusCell?: HTMLElement, colId?: string): HTMLElement[] {
    const orderedCandidates: HTMLElement[] = [];

    const addCandidate = (element?: HTMLElement | null): void => {
      if (!element || orderedCandidates.includes(element)) {
        return;
      }
      orderedCandidates.push(element);
    };

    const addTooltipHost = (element?: HTMLElement | null): void => {
      if (!element) {
        return;
      }

      const tooltipHost = element.closest('[tooltip]') as HTMLElement | null;
      if (tooltipHost) {
        addCandidate(tooltipHost);
      }
    };

    const rendererName = colId ? this.gridColumnApi?.getColumn(colId)?.getColDef?.()?.cellRenderer : undefined;
    if (rendererName === 'actionRenderer' || rendererName === 'lockStatusRenderer') {
      addTooltipHost(preferredTarget);

      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement && focusCell?.contains(activeElement)) {
        addTooltipHost(activeElement);
      }

      return orderedCandidates;
    }

    const addFromElement = (element?: HTMLElement | null): void => {
      if (!element) {
        return;
      }

      addCandidate(element);

      const nearbyHosts = Array.from(
        element.querySelectorAll('[tooltip], ion-icon, img, span, a[role="button"], button, [tabindex]')
      ) as HTMLElement[];
      nearbyHosts.forEach((host) => addCandidate(host));
    };

    if (preferredTarget) {
      let current: HTMLElement | null = preferredTarget;
      while (current && current !== focusCell) {
        addFromElement(current);
        current = current.parentElement;
      }
    }

    addFromElement(preferredTarget);
    addFromElement(focusCell);

    if (preferredTarget && focusCell) {
      const nearestFocusable = preferredTarget.closest('a[role="button"], a, button, [tabindex], ion-icon, img, span') as HTMLElement | null;
      addFromElement(nearestFocusable || preferredTarget);

      const focusablesInCell = Array.from(
        focusCell.querySelectorAll('a[role="button"], a, button, [tabindex], ion-icon, img, span')
      ) as HTMLElement[];
      focusablesInCell.forEach((element) => addFromElement(element));
    }

    return orderedCandidates;
  }

  private getFocusStorageKey(): string {
    const routeKey = window.location.pathname || 'unknown-route';
    const componentKey = this.parentComponent || this.gridConfigItems?.title || 'shared-grid';
    return `grid-last-focus:${routeKey}:${componentKey}`;
  }

  private updateGridFocusKey(): void {
    const routeKey = window.location.pathname || 'unknown-route';
    const componentKey = this.parentComponent || this.gridConfigItems?.title || 'shared-grid';
    this.gridFocusKey = `${routeKey}:${componentKey}`;
  }

 

  private saveLastFocusedCell(rowIndex: number, colId: string): void {
    if (rowIndex == null || !colId) {
      return;
    }
 sessionStorage.setItem(this.getFocusStorageKey(), JSON.stringify({ rowIndex, colId }));
  }

    private clearStoredCellFocus(): void {
      sessionStorage.removeItem(this.getFocusStorageKey());
    }

  private getLastFocusedCell(): { rowIndex: number; colId: string } | null {
    const raw = sessionStorage.getItem(this.getFocusStorageKey());
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);

      if (typeof parsed?.rowIndex === 'number' && typeof parsed?.colId === 'string' && parsed.colId) {
        return { rowIndex: parsed.rowIndex, colId: parsed.colId };
      }
    } catch (_err) {
      // Ignore malformed storage and continue without restore.
    }

    return null;
  }

  private handleReturnFocusRestore(allowStoredCellRestore = false): void {
    const pendingFocus = GridActionFocusRestore.get();
    if (pendingFocus && this.isPendingActionFocusForThisGrid(pendingFocus)) {
      this.tryRestorePendingActionFocus(pendingFocus);

      const remainingPendingFocus = GridActionFocusRestore.get();
      if (remainingPendingFocus && this.isPendingActionFocusForThisGrid(remainingPendingFocus)) {
        return;
      }
    }

    if (!allowStoredCellRestore) {
      return;
    }

    this.tryRestoreStoredCellFocus();
  }

  private isPendingActionFocusForThisGrid(pendingFocus: { gridFocusKey?: string; gridDomIndex: number }): boolean {
    if (pendingFocus.gridFocusKey) {
      return pendingFocus.gridFocusKey === this.gridFocusKey;
    }

    const gridRoot = this.agGridElement?.nativeElement;
    if (!gridRoot) {
      return false;
    }

    const gridRoots = Array.from(document.querySelectorAll('.ag-theme-alpine')) as HTMLElement[];
    const currentGridDomIndex = gridRoots.indexOf(gridRoot);
    return pendingFocus.gridDomIndex !== -1 && currentGridDomIndex === pendingFocus.gridDomIndex;
  }

  private tryRestoreStoredCellFocus(): void {
    if (!this.gridApi || !this.agGridElement?.nativeElement || GridActionFocusRestore.hasOpenOverlay()) {
      return;
    }

    const activeElement = document.activeElement as HTMLElement | null;
    const focusLost = !activeElement || activeElement === document.body || activeElement === document.documentElement;
    if (!focusLost) {
      return;
    }

    const storedFocus = this.getLastFocusedCell();
    if (!storedFocus) {
      return;
    }
this.restoreApproximateRowFocus(storedFocus.rowIndex, storedFocus.colId);
  }

  private restoreApproximateRowFocus(rowIndex: number, preferredColId: string): boolean {
    if (!this.gridApi) {
      return false;
    }

    const displayedRowCount = this.gridApi.getDisplayedRowCount();
    if (!displayedRowCount || displayedRowCount <= 0) {
      return false;
    }

    const targetRowIndex = Math.max(0, Math.min(rowIndex, displayedRowCount - 1));
    const displayedColumns = this.gridColumnApi?.getAllDisplayedColumns() || [];
    const fallbackColId = displayedColumns[0]?.getColId?.();
    const targetColId = preferredColId || fallbackColId;
    if (!targetColId) {
      return false;
    }

    this.gridApi.ensureIndexVisible(targetRowIndex, 'middle');
    this.gridApi.setFocusedCell(targetRowIndex, targetColId);

    const column = this.gridColumnApi?.getColumn(targetColId);
    const isActionColumn = column?.getColDef?.()?.cellRenderer === 'actionRenderer';
    if (isActionColumn) {
      setTimeout(() => this.focusFirstEnabledActionIcon(targetRowIndex, targetColId), 0);
    }

    return true;
  }

  private tryRestorePendingActionFocus(pendingFocus = GridActionFocusRestore.get()): void {
    if (!this.agGridElement?.nativeElement || GridActionFocusRestore.hasOpenOverlay()) {
      return;
    }

    if (!pendingFocus) {
      return;
    }
    if (!this.isPendingActionFocusForThisGrid(pendingFocus)) {
      return;
    }

    const gridRoot = this.agGridElement.nativeElement;

    const resolvedRowIndex = this.resolvePendingFocusRowIndex(pendingFocus);
    const targetRowIndex = resolvedRowIndex ?? pendingFocus.rowIndex;

    const rowSelector = `.ag-row[row-index="${targetRowIndex}"]`;
    const cellSelector = `.ag-cell[col-id="${pendingFocus.colId}"]`;
    const rowElements = Array.from(gridRoot.querySelectorAll(rowSelector)) as HTMLElement[];
    const actionCell = rowElements
      .map((rowElement) => rowElement.querySelector(cellSelector) as HTMLElement)
      .find((cell) => !!cell);

    if (!actionCell) {
      return;
    }

    const actionLinks = Array.from(actionCell.querySelectorAll('a[role="button"]:not(.disabled):not([hidden])')) as HTMLElement[];
    const restoreTarget = actionLinks[pendingFocus.actionIndex] || actionLinks[0];

    if (!restoreTarget) {
      this.restoreApproximateRowFocus(targetRowIndex, pendingFocus.colId);
      // this.clearStoredCellFocus();
      GridActionFocusRestore.clear();
      return;
    }

    this.clearPendingActionFocusForThisGrid();
    // this.clearStoredCellFocus();
    setTimeout(() => restoreTarget.focus(), 0);
  }

  private resolvePendingFocusRowIndex(pendingFocus: { rowIdentity?: Record<string, unknown>; rowIndex: number }): number | null {
    if (!this.gridApi || !pendingFocus?.rowIdentity || Object.keys(pendingFocus.rowIdentity).length === 0) {
      return pendingFocus?.rowIndex ?? null;
    }

    let matchedRowIndex: number | null = null;
    this.gridApi.forEachNode((node) => {
      if (matchedRowIndex != null || !node?.data) {
        return;
      }

      const isMatch = Object.entries(pendingFocus.rowIdentity).every(([key, value]) => node.data?.[key] === value);
      if (isMatch) {
        matchedRowIndex = node.rowIndex;
      }
    });

    return matchedRowIndex;
  }

  private rememberMouseActionFocus(params: any): void {
    const clickedElement = params?.event?.target as HTMLElement | null;
    const actionLink = clickedElement?.closest('a[role="button"]') as HTMLElement | null;
    const rowIndex = params?.node?.rowIndex;
    const colId = params?.column?.getColId?.();

    if (!actionLink || rowIndex == null || !colId) {
      return;
    }

    const actionCell = actionLink.closest('.ag-cell') as HTMLElement | null;
    if (!actionCell) {
      return;
    }

    const actionLinks = Array.from(actionCell.querySelectorAll('a[role="button"]:not(.disabled):not([hidden])')) as HTMLElement[];
    const actionIndex = actionLinks.indexOf(actionLink);
    if (actionIndex === -1) {
      return;
    }

    const gridRoot = this.agGridElement?.nativeElement;
    const gridRoots = Array.from(document.querySelectorAll('.ag-theme-alpine')) as HTMLElement[];
    const gridDomIndex = gridRoot ? gridRoots.indexOf(gridRoot) : -1;
    const gridFocusKey = this.gridFocusKey || gridRoot?.getAttribute('data-grid-focus-key') || undefined;
    const rowData = params?.data || {};
    const identityKeys = ['id', 'uuid', '_id', 'project_id', 'projectId', 'service_product_id', 'serviceProductId', 'roadmapId', 'machine_id', 'machineId', 'userId', 'email', 'name'];
    const rowIdentity = identityKeys.reduce((identity, key) => {
      if (rowData[key] !== undefined && rowData[key] !== null && rowData[key] !== '') {
        identity[key] = rowData[key];
      }
      return identity;
    }, {} as Record<string, unknown>);

    GridActionFocusRestore.set({
      actionIndex,
      colId,
      expiresAt: Date.now() + 180000,
      gridFocusKey,
      gridDomIndex,
      rowIdentity,
      rowIndex,
    });
  }

  clearSearchedInput() {
    this.searchText = '';
    this.lastSearchText = ''; // Update last search text to ensure clear action works
    
    if(this.parentComponent != '' && this.parentComponent != 'usermanagement') {
      this.searchTextChanged.emit(this.searchText); 
    } else {
      this.refreshData();
    }
  }

  /**
   * Handle AG Grid filter changed event
   * Emits filter model to parent component when filters are applied
   */
  public handleFilterChanged(event: any) {
    if (this.gridApi) {
      clearTimeout(this.filterModifiedDebounceTimeout);

      if (this.isRestoringFilterState) {
        return;
      }

      const filterModel = this.gridApi.getFilterModel();
      this.filters = (filterModel && Object.keys(filterModel).length > 0) ? filterModel : null;
      this.onFilterChanged.emit(this.filters);
    }
  }

  public handleFilterModified(event: any) {
    const shouldAutoApplyFilter = this.parentComponent === 'usermanagement';
    if (!shouldAutoApplyFilter || !this.gridApi) {
      return;
    }

    if (this.isRestoringFilterState) {
      return;
    }

    clearTimeout(this.filterModifiedDebounceTimeout);
    this.filterModifiedDebounceTimeout = setTimeout(() => {
      if (this.isRestoringFilterState) {
        return;
      }

      let filterModelChanged = false;

      this.columnDefs.forEach(columnDef => {
        if (!columnDef.field || !columnDef.filter) {
          return;
        }

        const filterInstance = this.gridApi.getFilterInstance(columnDef.field) as any;
        if (filterInstance?.applyModel) {
          filterModelChanged = filterInstance.applyModel('ui') || filterModelChanged;
        }
      });

      if (filterModelChanged) {
        this.gridApi.onFilterChanged();
      }
    }, 500);
  }

  public restoreFloatingFilterState(): void {
    if (!this.gridApi || !this.filters || Object.keys(this.filters).length === 0) {
      return;
    }

    this.isRestoringFilterState = true;
    this.gridApi.setFilterModel(this.filters);
    this.gridApi.refreshHeader();

    requestAnimationFrame(() => {
      this.isRestoringFilterState = false;
    });
  }

  private setWidthAndHeight() {
    setTimeout(() => {
      const height = 400;
      this.gridstyle = {
        marginTop: '0px',
        width: '100%',
        height: (height) + 'px',
        boxSizing: 'border-box'
      };

      this.scheduleViewportFillCheck();

    }, 100);
  }

  async openStatusPopover(ev: any) {
    const triggerElement = this.getPopoverTriggerElement(ev);
    let props: any;
    const type = this.gridConfigItems.menuType;
    switch (type) {
      case 1:
        props = { popoverMenu: popoverMenu.exportMenu, triggerElement };
        break;
      case 4:
        props = { popoverMenu: popoverMenu.gridMenu, triggerElement };
        break;


      default:
        break;
    }

    const popover = await this.popoverController.create({ mode: 'ios',
      component: StatusPopoverComponent,
      cssClass: 'popover-position',
      event: ev,
      translucent: true,
      componentProps: props,
      showBackdrop: false
    });
    await popover.present();
    await popover.onDidDismiss().then((result) => {
      const { data } = result;
      if (data && data == 'export') {
        this.exportData();
      }
      if (data && data == 'add_remove') {
        switch(type)
        {
          case 4: this.openAddOrRemoveColumnSelectorAlertModal('projectStatus');
                  break;
        }
      }
    });
  }

  private getPopoverTriggerElement(ev: any): HTMLElement | null {
    const eventTarget = ev?.target as HTMLElement | null;
    const activeElement = document.activeElement as HTMLElement | null;

    return this.resolveFocusableTrigger(eventTarget)
      || this.resolveFocusableTrigger(activeElement)
      || this.gridMoreIcon?.nativeElement
      || null;
  }

  private resolveFocusableTrigger(element: HTMLElement | null): HTMLElement | null {
    if (!element) {
      return null;
    }

    const triggerSelector = 'a[role="button"], button, [tabindex="0"], .ag-cell';
    const triggerElement = element.closest(triggerSelector) as HTMLElement | null;
    if (triggerElement && typeof triggerElement.focus === 'function') {
      return triggerElement;
    }

    return null;
  }

  async openAddOrRemoveColumnSelectorAlertModal(type) {
    const props = { columnSelectorFor: type };
    const modal = await this.modalController.create({
      component: AddRemoveColumnSelectorComponent,
      cssClass: 'add-remove-widget-class',
      backdropDismiss: false,
      showBackdrop: false,
      componentProps: props,
    });
    await modal.present();
    const {
      data: {
        columnSelectorSelectedValue: {
          isDataChangedForColumnSelector,
          columnSelectorWidgetFor,
        },
      },
    } = await modal.onWillDismiss();
    if (isDataChangedForColumnSelector && columnSelectorWidgetFor) {this.getColumnSelectorPermission([columnSelectorWidgetFor]);}
  }

  getColumnSelectorPermission(types) {
    const _self = this;
    if (types.includes('projectStatus')) {
      const data =
        _self.localSettingsSrv.getProjectStatusColumnSelectorWidgetStorageValue();
      if (isNotEmptyArray(data))
        {_self.checkProjectTrackerColumnSelectorPermission(data);}
    }
  }
  projectTrackerColumnSelectorPermission: any = {};
  //FNC TO CHECK PERMISSIONS PROJECT TRACKER FIELDS
  checkProjectTrackerColumnSelectorPermission(value) {
    const _self = this;
    value.forEach((_v) => {
      if (_v.val === 'pid')
        {_self.projectTrackerColumnSelectorPermission.projectId =
          _v.isChecked;}
      if (_v.val === 'pname')
        {_self.projectTrackerColumnSelectorPermission.projectName =
          _v.isChecked;}
      if (_v.val === 'cname')
        {_self.projectTrackerColumnSelectorPermission.customerName =
          _v.isChecked;}
      if (_v.val === 'custId')
        {_self.projectTrackerColumnSelectorPermission.customerId =
          _v.isChecked;}
      if (_v.val === 'contactName')
        {_self.projectTrackerColumnSelectorPermission.contactName =
          _v.isChecked;}
      if (_v.val === 'custAddress')
        {_self.projectTrackerColumnSelectorPermission.customerAddress =
          _v.isChecked;}
      if (_v.val === 'rgns')
        {_self.projectTrackerColumnSelectorPermission.regions  = _v.isChecked;}
      if (_v.val === 'pmname')
        {_self.projectTrackerColumnSelectorPermission.projectManName =
          _v.isChecked;}
      if (_v.val === 'prg')
        {_self.projectTrackerColumnSelectorPermission.projectProgress  =
          _v.isChecked;}
      if (_v.val === 'hrPrg')
        {_self.projectTrackerColumnSelectorPermission.projectHourProgress =
          _v.isChecked;}
      if (_v.val === 'etd')
        {_self.projectTrackerColumnSelectorPermission.endDate = _v.isChecked;}
      if (_v.val === 'status')
        {_self.projectTrackerColumnSelectorPermission.status = _v.isChecked;}
    });
  }

  private sortGrid(sortColumn, sortDirection) {
    this.gridApi.applyColumnState({
      state: [
        {
          colId: sortColumn,
          sort: sortDirection
        }
      ],
      defaultState: {
        sort: null
      }
    });
  }

  public setDataLoading(isDataLoading) {
    this.isDataLoading = isDataLoading;
    if (this.gridlastRowIndex > 0) {
      this.isDataLoading = false;
      this.gridApi.hideOverlay();
    }
    else if (this.isDataLoading) {
      this.gridApi.showLoadingOverlay();
    }
    else {
      this.gridApi.showNoRowsOverlay();
    }
  }

  public initializeGrid(isDataLoading = true) {
    if (this.isComponentDestroyed) {
      return;
    }

    const self = this;
    this.updateGridFocusKey();
    this.currentOffset = 0;
    this.isDataLoading = isDataLoading;
    
    // Clear any existing filters when reinitializing the grid (e.g., tab switch)
    this.filters = null;
    
    if (this.columnDefs && this.columnDefs.length > 0) {
      this.fnAddCheckboxColumn(this.columnDefs);

      this.overlayNoRowsTemplate = `
      <div class="text-center noDataAvailable w-100pc f-16 position-relative">
          ${this.translate.instant(this.gridConfigItems.noDataMessage)}
        </div>
      `;

      const noTooltipColumns = ['sales_organization_count','user_role','is_locked','isLocked'];
      this.columnDefs.forEach(columnDef => {
        if (!noTooltipColumns.includes(columnDef.field)) {
          columnDef.tooltipComponent = 'customTooltip';
          // Preserve explicit tooltipValueGetter (e.g. for translated values);
          // tooltipField would otherwise force ag-grid to use the raw field value.
          if (!columnDef.tooltipValueGetter) {
            columnDef.tooltipField = columnDef.field;
          }
        }
        if (!columnDef.headerComponentParams) {
          columnDef.headerComponentParams = {};
        }
        columnDef.headerComponentParams.rowCount = this.allGridData?.length;
      });
      
      // If grid already exists, update column definitions directly
      if (this.gridApi) {
        // Destroy all column filters first
        this.columnDefs.forEach(col => {
          if (col.field) {
            this.gridApi.destroyFilter(col.field);
          }
        });
        this.gridOptions = {
          ...this.gridOptions,
          columnDefs: this.columnDefs
        };
        this.gridApi.setColumnDefs([]);
        this.gridApi.setColumnDefs(this.columnDefs);
        this.gridApi.setFilterModel(null);
        this.resetDataSource();
        if (this.resetScrollOnVisible) {
          this.scheduleScrollReset();
        }
        return;
      }
      
      this.gridOptions = {
        rowSelection: 'multiple',
        rowHeight: self.gridConfigItems.rowHeight,
        headerHeight: 40,
        floatingFiltersHeight: 40,
        groupHeaderHeight: 0,
        cacheBlockSize: 10,
        maxBlocksInCache: 3,
        rowModelType: 'infinite',
        pagination: false,
        paginationAutoPageSize: true,
        alwaysShowHorizontalScroll: true,
        localeText: this.buildAgGridLocaleText(),
        onBodyScroll: this.onGridBodyScroll,
        onFilterChanged: (event) => this.handleFilterChanged(event),
        onFilterModified: (event) => this.handleFilterModified(event),
        components: {
            progressBarRenderer: ProgressBarRendererComponent,
            buttonRenderer: ButtonsRendererComponent,
            actionRenderer: ActionsRendererComponent,
            fieldValueRenderer: FieldValueRendererComponent,
            dateRenderer:DateRendererComponent,
            cbxRenderer: CheckBoxRendererComponent,
            userNameRenderer: UserNameRendererComponent,
            countryNameRenderer: CountryFlagRendererComponent,
            hoverDataRenderer : HoverDataRendererComponent,
            hoverRoleRenderer: HoverRoleRendererComponent,
            radioRenderer: RadioRendererComponent,
          lockStatusRenderer: LockStatusRendererComponent,
          controlledTextFloatingFilter: ControlledTextFloatingFilterComponent
        },
        suppressHeaderFocus: this.skipHeaderOnTab,
        suppressCellFocus: this.enableBodyCellKeyboardNavigation === undefined
          ? self.gridConfigItems.suppressCellSelection
          : !this.enableBodyCellKeyboardNavigation,
        suppressRowClickSelection: self.gridConfigItems.suppressRowClickSelection,
        tabToNextCell: (params: any) => {
          const previousCell = params?.previousCellPosition;
          const firstColumn = this.getFirstDisplayedColumn();
          const firstRowIndex = this.gridApi?.getFirstDisplayedRow?.() ?? -1;

          if (
            params?.backwards &&
            !this.skipHeaderOnTab &&
            previousCell?.rowIndex === firstRowIndex &&
            previousCell?.column?.getColId?.() === firstColumn?.getColId?.()
          ) {
            const lastHeaderColumn = this.getLastNavigableHeaderColumn();
            if (lastHeaderColumn) {
              return {
                rowIndex: -1,
                rowPinned: null,
                column: lastHeaderColumn,
              };
            }
          }

          const nextCell = params?.nextCellPosition;
          if (nextCell?.column?.getColDef?.()?.cellRenderer === 'actionRenderer') {
            const nextColId = nextCell.column.getColId?.();
            const nextRowIndex = nextCell.rowIndex;
            setTimeout(() => this.focusFirstEnabledActionIcon(nextRowIndex, nextColId), 0);
          }
          return nextCell;
        },
        defaultColDef: {
          flex: 1,
          sortable: true,
          resizable: true,
          filter: 'agTextColumnFilter',
          autoHeight: true
        },
        tooltipShowDelay: 0,
        suppressRowTransform: true,
        rowClassRules: {
          disabled(params) { return params && params.data && params.data.isDisabled; },

          'cursor-pointer'(params) {
            return !self.gridConfigItems.suppressRowClickSelection;
          },
          'row-highlight'(params) {
            return params && params.data && params.data.isRowHighlighted;
          },
          'soft-deleted-row'(params) {
            return params && params.data && params.data.isSoftDeleted;
          },
          'ag-row-highlight-even'(params) {
            if (params && params.data && params.data.isRowHighlighted) {
              return false;
            }
            else {
              if (params && params.data && !params.data.customeRowClass)
                {return params.node.rowIndex % 2 === 0;}
            }
          },
          'ag-row-highlight-odd'(params) {
            if (params && params.data && params.data.isRowHighlighted) {
              return false;
            }
            else {
              if (params && params.data && !params.data.customeRowClass)
                {return params.node.rowIndex % 2 != 0;}
            }
          },
        },
        columnDefs: this.columnDefs
      };
      this.setWidthAndHeight();
    }
  }

  public updateGridColumnDefinitions(columnDefs) {
    if (this.gridApi) {
      // Capture filter model before updating columns
      const currentFilterModel = this.filters || this.gridApi.getFilterModel();
      const hasFilterModel = currentFilterModel && Object.keys(currentFilterModel).length > 0;

      if (columnDefs && columnDefs.length > 0) {
        this.columnDefs = columnDefs;
        this.fnAddCheckboxColumn(columnDefs);
      }

      this.gridOptions = {
        ...this.gridOptions,
        columnDefs: this.columnDefs
      };

      const noTooltipColumnsRefresh = ['sales_organization_count','user_role','is_locked','isLocked'];
      this.columnDefs.forEach(columnDef => {
        if (!noTooltipColumnsRefresh.includes(columnDef.field) && !columnDef.tooltipValueGetter) {
          columnDef.tooltipField = columnDef.field;
        }
        if (!columnDef.headerComponentParams) {
          columnDef.headerComponentParams = {};
        }
        columnDef.headerComponentParams.rowCount = this.allGridData.length;
      });

      // Only reset column definitions if new columns are provided
      // This prevents destroying filter state when just updating row counts
      if (columnDefs && columnDefs.length > 0) {
        this.gridApi.setColumnDefs([]);
        this.gridApi.setColumnDefs(this.columnDefs);

        // Restore filter model after column definitions are set
        if (hasFilterModel) {
          this.filters = currentFilterModel;
          setTimeout(() => {
            if (this.gridApi) {
              this.gridApi.setFilterModel(currentFilterModel);
              // Force refresh the floating filters to show the values
              this.gridApi.refreshHeader();
            }
          }, 100);
        }
      } else {
        // Just refresh header to update row counts without destroying filters
        this.gridApi.refreshHeader();
      }
    }
  }

  public fnAddCheckboxColumn(columnDefs) {
    const self = this;
    if (self.gridConfigItems.enableRowSelection) {
      console.log('In add checkbox column');
      if (!this.columnDefs.find(c => c.field == 'selected')) {
        const cbxColumn = {
          headerName: '',
          field: 'selected',
          cellRenderer: 'cbxRenderer',
          sortable: false,
          resizable: false,
          width:60,
          maxWidth: 60,
          pinned: 'left',
          suppressMenu: true,
          headerComponentParams: {
            isCheckbox: true,
            isAllChecked: false,
            isCheckAllAllowed: self.gridConfigItems.isCheckAllAllowed,
            onCheckAll: (event) => this.onAllChecked(event.target.checked)
          },
          suppressMovable: true,
          lockPosition: true,
          lockPinned: true,
          tooltipField: 'selected'
        };
        this.columnDefs.unshift(cbxColumn);
      }
    }
  }

  public refreshGridData(newData?, hasMoreItems?) {
    if (this.isComponentDestroyed) {
      return;
    }

    this.overlayNoRowsTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative">
        ${this.translate.instant(this.gridConfigItems.noDataMessage)}
      </div>
    `;

    if (this.gridApi && !this.isGridDestroyed()) {
      this.isDataLoading = false;
      this.isRefresh = true;

      // Capture current scroll position
      const gridBody = this.agGridElement?.nativeElement?.querySelector('.ag-body-viewport') as HTMLElement | null;
      const scrollTop = gridBody ? gridBody.scrollTop : 0;

      // Store filter model before any reset operations
      const currentFilterModel = this.filters || this.gridApi.getFilterModel();
      if (currentFilterModel && Object.keys(currentFilterModel).length > 0) {
        this.filters = currentFilterModel;
      }

      this.resetSortModel();
      this.resetDataSource();

      this.columnDefs.forEach(columnDef => {
        if (!columnDef.headerComponentParams) {
          columnDef.headerComponentParams = {};
        }
        columnDef.headerComponentParams.rowCount = this.allGridData.length;
      });

      this.updateGridColumnDefinitions(null);

      // Update grid data
      if (newData && hasMoreItems) {
        this.hasMoreItems = hasMoreItems;
        this.allGridData = [...newData];
        setTimeout(() => {
          if (gridBody && this.agGridElement?.nativeElement?.contains(gridBody)) {
            gridBody.scrollTop = scrollTop;
          }
        }, 100);
      }
    } else {      
      this.initializeGrid();      
    }    
    this.sharedValue.isGridDataLoading = false;
 }

  public clearFilters() {
    if (this.gridApi) {
      // Destroy all column filters to clear UI state
      this.columnDefs.forEach(col => {
        if (col.field) {
          this.gridApi.destroyFilter(col.field);
        }
      });
      this.gridApi.setFilterModel(null);
      this.filters = null;
      this.resetDataSource();
    }
  }

  public resetSortModel() {
    this.sortGrid(this.gridConfigItems.defaultSortColumn, this.gridConfigItems.defaultSortOrder);
  }

  public setSortModel(sortColumn, sortDirection) {
    this.sortGrid(sortColumn, sortDirection);
  }

 
  public fnAppendAllDataInGrid(gridDataForAppend, hasMoreItems?: boolean) {
    const incoming = Array.isArray(gridDataForAppend) ? gridDataForAppend : [];
    const previousLength = this.allGridData.length;

    const combinedData = [...this.allGridData, ...incoming];
    const potentialKeys = [
      'machineId', 'id', 'roadmap_id',
      // camelCase (project/order management)
      'project_Id', 'offer_Id', 'order_Id',
      // lowercase (offer management uses these)
      'offer_id', 'project_id', 'order_id', 'service_id'
    ];
    const uniqueKey = potentialKeys.find(key => incoming[0]?.hasOwnProperty(key));
    this.allGridData = uniqueKey
    ? Array.from(
        new Map(combinedData.map(item => [item[uniqueKey], item])).values()
      )
    : combinedData;

    // Only stop infinite scroll when the server clearly returned nothing new.
    // (Do not infer end-of-list from a short page; backends may return
    //  varying page sizes and the previous behaviour was to keep paging until
    //  an empty response.)
    if (typeof hasMoreItems === 'boolean') {
      this.hasMoreItems = hasMoreItems;
    } else if (incoming.length === 0 || this.allGridData.length === previousLength) {
      this.hasMoreItems = false;
    }

    this.allGridData = this.sortByKey(this.allGridData, this.gridSortColumn, this.gridSortOrder);
    if (this.allGridData.length == 0) {
      this.sharedValue.isGridDataLoading = false;
    } else {
      const blockSize = this.gridOptions?.cacheBlockSize || 10;
      const hasPartialBlock = this.allGridData.length % blockSize !== 0;

      // `setRowCount` alone can leave partially filled rendered blocks with
      // temporary blank rows in some ag-grid versions. When total rows do not
      // align to cache block boundaries, refresh cache with preserved scroll.
      if (hasPartialBlock) {
        this.refreshData({ preserveScroll: true });
      } else {
        this.extendInfiniteRowCount();
      }
    }

    this.scheduleViewportFillCheck();

    // Always release the in-flight guard so the next scroll can load more.
    this.isLoadingMore = false;
  }

  public fnReplaceAllDataInGrid(gridDataForReplace, hasMoreItems: boolean = true) {
    // Replace the entire data set instead of appending (used for search scenarios)
    this.allGridData = gridDataForReplace || [];
    this.currentOffset = 0; // Reset offset for new search
    // Reset infinite-scroll state on a fresh data load. Assume more items may
    // be available; the next append/empty response will flip this off.
    this.isLoadingMore = false;
    this.hasMoreItems = hasMoreItems;

    this.allGridData = this.sortByKey(this.allGridData, this.gridSortColumn, this.gridSortOrder);
    // Always refresh the grid, even when data is empty, to update the display
    this.refreshData();
    this.scheduleViewportFillCheck();
  }

  public removeRow(data) {
    const rowNode = this.gridApi.getRowNode(data[this.gridConfigItems.rowKey].toString());
    this.gridData.splice(rowNode.rowIndex, 0, rowNode);
    this.gridApi.refreshInfiniteCache();
  }

  public uncheckItems(checkedItemIds) {
    if (this.gridData && this.gridData.length > 0) {
      const checkedRows = this.gridData.filter(g => checkedItemIds.indexOf(g[this.gridConfigItems.rowKey]) > -1);
      for (let i = 0; i < checkedRows.length; i++) {
        checkedRows[i].selected = false;
      }
      this.checkUncheckAll();
    }
  }

  public checkItems(checkedItemIds) {
    if (this.gridData && this.gridData.length > 0) {
      const checkedRows = this.gridData.filter(g => checkedItemIds.indexOf(g[this.gridConfigItems.rowKey]) > -1);
      for (let i = 0; i < checkedRows.length; i++) {
        checkedRows[i].selected = true;
      }
      this.checkUncheckAll();
    }
  }

  checkUncheckAll() {
    if (this.gridApi && this.gridConfigItems.enableRowSelection) {
      if (this.gridData && this.gridData.length > 0) {
        const checkedcount = this.gridData.filter(r => r.selected).length;
        if (checkedcount == this.gridData.length) {
          this.isAllChecked = true;
        }
        else {
          this.isAllChecked = false;
        }
        // this.gridApi.refreshHeader();
        this.refreshCheckbox();
      }
    }
  }

  public refreshCheckbox() {
    if (this.gridApi) {
      if (this.gridConfigItems.enableRowSelection) {
        const cbxColumn = this.columnDefs.find(c => c.field == 'selected');
        if (cbxColumn) {
          cbxColumn.headerComponentParams.isCheckAllAllowed = this.gridConfigItems.isCheckAllAllowed;
          cbxColumn.headerComponentParams.isAllChecked = this.isAllChecked;
        }
        this.updateGridColumnDefinitions(null);
      }
    }
  }

  public uncheckAll() {
    if (this.gridData) {
      for (let i = 0; i < this.gridData.length; i++) {
        this.gridData[i].selected = false;
      }
      this.checkUncheckAll();
    }
  }

  setDataInGrid(data) {
    if (data) {
      const rowNode = this.gridApi.getRowNode(data[this.gridConfigItems.rowKey].toString());
      const newData = data;
      rowNode.setData(newData);
      if (data.IsRowOnTop) {
        this.allGridData.splice(rowNode.rowIndex, 1);
        this.allGridData.splice(0, 0, rowNode.data);
      }
      else {
        this.allGridData.splice(rowNode.rowIndex, 1, rowNode.data);
      }
      this.refreshData();
    }
  }

  reorderUpdatedRecords(dataArray: any[]) {
    dataArray.forEach(data => {
      const rowNode = this.gridApi.getRowNode(data[this.gridConfigItems.rowKey].toString());
      const newData = data;
      rowNode.setData(newData);
      this.allGridData.splice(rowNode.rowIndex, 1);
      this.allGridData.splice(0, 0, rowNode.data);
    });
    this.refreshData();
  }

  onGridSearchTextChange(searchText) {
    if (typeof searchText === 'string') {    
      this.searchText = searchText;
    } else {
      this.searchText = searchText.detail.value;
    }
    
    // Check if the search text has actually changed to prevent duplicate calls
    if (this.searchText === this.lastSearchText) {
      return;
    }
    
    // Update the last search text
    this.lastSearchText = this.searchText;
    
    // Clear existing timeout to prevent multiple rapid calls
    clearTimeout(this.searchDebounceTimeout);
    
    // Add additional debouncing as a safety measure (ion-searchbar already has debounce="800")
    this.searchDebounceTimeout = setTimeout(() => {
      if(this.parentComponent != '' && this.parentComponent !='usermanagement') {
        this.searchTextChanged.emit(this.searchText); 
      } else {
        this.refreshData();
      }
    }, 600); // Short additional delay since ion-searchbar already debounces
  }

  oncolumnResized(params) {
    this.autosizeHeaders(params);
  }

  onCellClicked(params) {
    if (this.tooltipShowTimeout) {
      clearTimeout(this.tooltipShowTimeout);
      this.tooltipShowTimeout = undefined;
    }
    this.clearFocusedCellTooltip();

    if (params.column.colId === 'selected') {
      if (params.data.isCheckboxDisabled) {
        return;
      }
      params.data.selected = params.event.target.checked;
      this.checkUncheckAll();
    }
    
    // Check if click came from an action cell to prevent row-level action pollution
    const clickedElement = params?.event?.target as HTMLElement;
    const isActionCellClick = clickedElement?.closest('a[role="button"]') !== null || 
                              params?.colDef?.cellRenderer === 'actionRenderer';

    if (isActionCellClick) {
      this.rememberMouseActionFocus(params);
    }
    
    // If not an action cell click but userAction exists on row data, clear it to prevent pollution
    if (!isActionCellClick && params?.data?.userAction) {
      delete params.data.userAction;
    }
    
    this.onCellClick.emit(params);
  }

  onCellKeyDown(params) {
    const keyboardEvent = params?.event as KeyboardEvent;
    const key = keyboardEvent?.key;

    if (!keyboardEvent || (key !== 'Enter' && key !== 'NumpadEnter' && key !== ' ' && key !== 'Spacebar')) {
      return;
    }

    if (this.enableCheckboxEnterToggle && (key === 'Enter' || key === 'NumpadEnter') && params?.column?.colId === 'selected') {
      if (params?.data?.isCheckboxDisabled) {
        return;
      }

      const nextSelectedValue = !params?.data?.selected;
      params.data.selected = nextSelectedValue;
      params.node?.setDataValue?.('selected', nextSelectedValue);
      if (params?.data?.userAction) {
        delete params.data.userAction;
      }
      this.checkUncheckAll();

      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      this.onCellClick.emit(params);
      return;
    }

    const target = keyboardEvent.target as HTMLElement;
    if (params?.colDef?.cellRenderer === 'actionRenderer') {
      const focusedActionIcon = target?.closest('a[role="button"]') as HTMLElement;
      if (focusedActionIcon) {
        // Let the action renderer handle Enter/Space for the currently focused icon.
        return;
      }

      const parentCell = target?.closest('.ag-cell') as HTMLElement;
      const firstEnabledAction = parentCell?.querySelector('a[role="button"]:not(.disabled):not([hidden])') as HTMLElement;

      if (firstEnabledAction) {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        firstEnabledAction.focus();
        return;
      }
    }

    if (target && ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName)) {
      return;
    }

    // Prevent stale row-level action replay when Enter is pressed on non-action cells.
    if (params?.colDef?.cellRenderer !== 'actionRenderer' && params?.data?.userAction) {
      delete params.data.userAction;
    }

    keyboardEvent.preventDefault();
    this.onCellClick.emit(params);
  }

  onCellFocused(event: any) {
    this.clearFocusedCellTooltip();

    const rowIndex = event?.rowIndex;
    const column = event?.column;
    const colId = column?.getColId?.();
    if (rowIndex != null && colId) {
      this.saveLastFocusedCell(rowIndex, colId);
    }

    if (!this.enableBodyCellKeyboardNavigation || !this.gridApi || !this.agGridElement?.nativeElement) {
      return;
    }

    if (rowIndex == null || !column) {
      return;
    }

    const colDef = column.getColDef?.();
    if (colDef?.cellRenderer !== 'actionRenderer') {
      return;
    }

    setTimeout(() => {
      const colId = column.getColId?.();
      if (!colId) {
        return;
      }
      this.focusFirstEnabledActionIcon(rowIndex, colId);
    }, 20);
  }

  private focusFirstEnabledActionIcon(rowIndex: number, colId: string) {
    if (rowIndex == null || !colId || !this.agGridElement?.nativeElement) {
      return;
    }

    const rowSelector = `.ag-row[row-index="${rowIndex}"]`;
    const cellSelector = `.ag-cell[col-id="${colId}"]`;
    const rowElements = Array.from(this.agGridElement.nativeElement.querySelectorAll(rowSelector)) as HTMLElement[];
    const actionCell = rowElements
      .map((rowElement) => rowElement.querySelector(cellSelector) as HTMLElement)
      .find((cell) => !!cell) as HTMLElement | undefined;

    if (!actionCell) {
      return;
    }

    const firstEnabledAction = actionCell.querySelector('a[role="button"]:not(.disabled):not([hidden])') as HTMLElement;
    firstEnabledAction?.focus();
  }

  onGridKeyDown(event: KeyboardEvent) {
    if (['Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      AppGridComponent.isKeyboardNavigation = true;
      this.isKeyboardFocusVisible = true;
    }

    if (this.bodyOnlyTabNavigation && event.key === 'Tab' && this.handleGridBoundaryTab(event)) {
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    const headerCell = target.closest('.ag-header-cell[role="columnheader"]') as HTMLElement;
    if (!headerCell) {
      return;
    }

    if (this.handleHeaderTabNavigation(event, headerCell)) {
      return;
    }

    const firstHeaderCell = this.getFirstNavigableHeaderCell();
    if (this.bodyOnlyTabNavigation && event.shiftKey && firstHeaderCell && headerCell === firstHeaderCell) {
      // Always block AG Grid's own Shift+Tab action so it cannot cycle focus
      // back inside the grid (e.g. to the last body cell) after we exit.
      event.preventDefault();
      event.stopPropagation();

      if (this.gridMoreIcon?.nativeElement && !this.gridMoreIcon.nativeElement.hidden) {
        this.gridMoreIcon.nativeElement.focus();
        return;
      }

      this.moveFocusOutsideGrid(true);
    }
  }

  onGridFocusIn(event: FocusEvent) {
    this.isKeyboardFocusVisible = AppGridComponent.isKeyboardNavigation;

    this.handleReturnFocusRestore(false);

    const target = event.target as HTMLElement | null;
    if (target && !this.isDispatchingTooltipFocus) {
      const focusedCell = target.closest('.ag-cell') as HTMLElement | null;
      if (focusedCell) {
        const rowElement = focusedCell.closest('.ag-row[row-index]') as HTMLElement | null;
        const rowIndex = Number(rowElement?.getAttribute('row-index'));
        const colId = focusedCell.getAttribute('col-id') || '';
        if (!Number.isNaN(rowIndex) && colId) {
          this.saveLastFocusedCell(rowIndex, colId);
          this.clearFocusedCellTooltip();
          if (this.tooltipShowTimeout) {
            clearTimeout(this.tooltipShowTimeout);
          }
          this.tooltipShowTimeout = setTimeout(() => {
            this.showTooltipForFocusedCell(rowIndex, colId, target);
          }, 0);
        }
      } else {
        this.clearFocusedCellTooltip();
      }
    }

    if (!this.bodyOnlyTabNavigation || !this.gridApi || !this.gridColumnApi) {
      return;
    }

    if (!target) {
      return;
    }

      // If focus moved FROM within the grid (relatedTarget inside gridRoot), this is
      // AG Grid's own internal cycling (Tab on last body cell → header/tab-guard).
      // onGridKeyDown's boundary handler owns that transition. Do NOT schedule a new
      // firstHeaderCell.focus() here or it will fire ~16ms later and steal focus back
      // from whatever element moveFocusOutsideGrid() correctly targeted.
      const gridRoot = this.agGridElement?.nativeElement;
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      if (relatedTarget && gridRoot && gridRoot.contains(relatedTarget)) {
        return;
      }

      // .ag-tab-guard is AG Grid's invisible sentinel at the start/end of the grid.
      // It has tabindex="0" so isMeaningfulGridControl() would accept it, but it is
      // not a real interactive element — always redirect past it.
      const isTabGuard = !!target.closest('.ag-tab-guard');
    if (!isTabGuard && (target.closest('.ag-cell') || this.isMeaningfulGridControl(target))) {
      return;
    }

    if (!this.skipHeaderOnTab) {
      const firstHeaderCell = this.getFirstNavigableHeaderCell();
      if (firstHeaderCell) {
        firstHeaderCell.focus();
        return;
      }
    }

    this.focusGridBodyCell(this.gridApi.getFirstDisplayedRow(), this.getFirstDisplayedColumnId());
  }

  private handleGridBoundaryTab(event: KeyboardEvent): boolean {
    if (!this.gridApi || !this.gridColumnApi) {
      return false;
    }

    // IMPORTANT: only use real DOM position — never fall back to getFocusedCell()
    // because that value is stale when focus is on a header or tab-guard sentinel.
    const targetCell = this.getBodyCellFromEventTarget(event.target as HTMLElement | null);
    if (!targetCell) {
      // Focus is not in a body cell (header, tab-guard, overlay …).
      // Let onGridKeyDown's header-navigation branch handle it.
      return false;
    }

    const firstColumnId = this.getFirstDisplayedColumnId();
    const lastColumnId = this.getLastDisplayedColumnId();
    const firstRowIndex = this.gridApi.getFirstDisplayedRow();
    const lastRowIndex = this.gridApi.getLastDisplayedRow();

    if (firstColumnId == null || lastColumnId == null || firstRowIndex < 0 || lastRowIndex < 0) {
      return false;
    }

    const { columnId: focusedColumnId, rowIndex: focusedRowIndex } = targetCell;
    const isAtFirstCell = event.shiftKey && focusedRowIndex === firstRowIndex && focusedColumnId === firstColumnId;
    const isAtLastCell = !event.shiftKey && focusedRowIndex === lastRowIndex && focusedColumnId === lastColumnId;

    if (!isAtFirstCell && !isAtLastCell) {
      return false;
    }

    // We are definitely at a grid boundary — always prevent AG Grid from cycling.
    event.preventDefault();
    event.stopPropagation();

    if (isAtFirstCell && !this.skipHeaderOnTab) {
      const headerCell = this.getLastNavigableHeaderCell();
      if (headerCell) {
        headerCell.focus();
        return true;
      }
    }

    this.moveFocusOutsideGrid(event.shiftKey);
    return true;
  }

  private moveFocusOutsideGrid(backwards: boolean): boolean {
    const gridRoot = this.agGridElement?.nativeElement;
    if (!gridRoot) {
      return false;
    }

    // Query all potentially-focusable elements including Ionic web-component hosts
    // (ion-button etc. whose real <button> lives in Shadow DOM and is invisible to
    // attribute selectors like button:not([disabled])).
    const candidateSelector = [
      'a', 'button', 'input', 'select', 'textarea', '[tabindex]',
      'ion-button', 'ion-back-button', 'ion-input', 'ion-select',
      'ion-checkbox', 'ion-toggle', 'ion-radio', 'ion-searchbar',
      'ion-fab-button', 'ion-segment-button',
    ].join(', ');

    const focusableElements = Array.from(document.querySelectorAll<HTMLElement>(candidateSelector))
      .filter((element) => {
        const tag = element.tagName;
        const isIonicHost = tag.startsWith('ION-');
        if (element.tabIndex < 0 && !isIonicHost) { return false; }
        if (element.hasAttribute('disabled') || (element as any).disabled === true) { return false; }
        if (gridRoot.contains(element)) { return false; } // exclude grid-internal nodes
        return this.isVisibleFocusable(element);
      });

    // Use DOM order (compareDocumentPosition) instead of the index of
    // document.activeElement — the active element is often a grid-internal
    // renderer widget that does not appear at a predictable position in the list.
    if (backwards) {
      // Last external element that comes BEFORE gridRoot in document order
      for (let i = focusableElements.length - 1; i >= 0; i--) {
        const el = focusableElements[i];
        if (el.compareDocumentPosition(gridRoot) & Node.DOCUMENT_POSITION_FOLLOWING) {
          if (this.focusCandidateElement(el)) {
            return true;
          }
        }
      }
    } else {
      // First external element that comes AFTER gridRoot in document order
      for (let i = 0; i < focusableElements.length; i++) {
        const el = focusableElements[i];
        if (el.compareDocumentPosition(gridRoot) & Node.DOCUMENT_POSITION_PRECEDING) {
          if (this.focusCandidateElement(el)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  private isVisibleFocusable(element: HTMLElement): boolean {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  private focusCandidateElement(element: HTMLElement): boolean {
    // Most native controls and custom elements with positive tabIndex.
    if (element.tabIndex >= 0) {
      element.focus();
      return document.activeElement === element || element.contains(document.activeElement as Node);
    }

    // Ionic buttons host their native focus target in shadow DOM.
    const tagName = element.tagName;
    if (tagName === 'ION-BUTTON' || tagName === 'ION-BACK-BUTTON') {
      const shadowButton = (element as any).shadowRoot?.querySelector?.('button') as HTMLButtonElement | null;
      if (shadowButton && !shadowButton.disabled) {
        shadowButton.focus();
        // For shadow DOM focus, document.activeElement is usually the host element,
        // while the actual focused node is shadowRoot.activeElement.
        const shadowActive = (element as any).shadowRoot?.activeElement as HTMLElement | null;
        return document.activeElement === element || shadowActive === shadowButton;
      }
    }

    return false;
  }

  private isMeaningfulGridControl(element: HTMLElement): boolean {
    return !!element.closest('a[role="button"], button, input, textarea, select, [tabindex="0"]');
  }

  private focusGridBodyCell(rowIndex: number, columnId: string | null): void {
    if (rowIndex < 0 || !columnId || !this.gridApi) {
      return;
    }

    setTimeout(() => {
      this.gridApi.ensureIndexVisible(rowIndex);
      this.gridApi.setFocusedCell(rowIndex, columnId);
      requestAnimationFrame(() => this.focusGridBodyCellElement(rowIndex, columnId));
    });
  }

  private focusGridBodyCellElement(rowIndex: number, columnId: string): void {
    const gridRoot = this.agGridElement?.nativeElement;
    if (!gridRoot) {
      return;
    }

    const rowSelector = `.ag-row[row-index="${rowIndex}"]`;
    const cellSelector = `.ag-cell[col-id="${columnId}"]`;
    const rowElements = Array.from(gridRoot.querySelectorAll(rowSelector)) as HTMLElement[];
    const targetCell = rowElements
      .map((rowElement) => rowElement.querySelector(cellSelector) as HTMLElement | null)
      .find((cell) => !!cell) as HTMLElement | undefined;

    targetCell?.focus({ preventScroll: true });
  }

  private getFirstDisplayedColumnId(): string | null {
    const columns = this.gridColumnApi?.getAllDisplayedColumns?.() || [];
    return columns.length ? columns[0].getColId() : null;
  }

  private getFirstDisplayedColumn(): any | null {
    const columns = this.gridColumnApi?.getAllDisplayedColumns?.() || [];
    return columns.length ? columns[0] : null;
  }

  private getLastDisplayedColumnId(): string | null {
    const columns = this.gridColumnApi?.getAllDisplayedColumns?.() || [];
    return columns.length ? columns[columns.length - 1].getColId() : null;
  }

  private getBodyCellFromEventTarget(target: HTMLElement | null): { rowIndex: number; columnId: string | null } | null {
    const cell = target?.closest('.ag-cell') as HTMLElement | null;
    if (!cell) {
      return null;
    }

    const row = cell.closest('.ag-row') as HTMLElement | null;
    const rowIndex = Number(row?.getAttribute('row-index'));
    const columnId = cell.getAttribute('col-id');

    if (Number.isNaN(rowIndex)) {
      return null;
    }

    return { rowIndex, columnId };
  }

  private handleHeaderTabNavigation(event: KeyboardEvent, headerCell: HTMLElement): boolean {
    const headerCells = this.getNavigableHeaderCells();
    if (!headerCells.length) {
      return false;
    }

    const currentIndex = headerCells.indexOf(headerCell);
    if (currentIndex === -1) {
      return false;
    }

    const nextIndex = currentIndex + (event.shiftKey ? -1 : 1);
    if (nextIndex >= 0 && nextIndex < headerCells.length) {
      event.preventDefault();
      event.stopPropagation();
      headerCells[nextIndex].focus();
      return true;
    }

    if (!event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      this.focusGridBodyCell(this.gridApi.getFirstDisplayedRow(), this.getFirstDisplayedColumnId());
      return true;
    }

    return false;
  }

  private getNavigableHeaderCells(): HTMLElement[] {
    const gridRoot = this.agGridElement?.nativeElement;
    if (!gridRoot) {
      return [];
    }

    const headerCells = Array.from(gridRoot.querySelectorAll<HTMLElement>('.ag-header-cell[role="columnheader"]'));
    const hasMeaningfulHeader = (cell: HTMLElement): boolean => {
      const text = cell.textContent?.trim();
      if (text) {
        return true;
      }

      const columnId = cell.getAttribute('col-id');
      if (!columnId || !this.gridColumnApi) {
        return false;
      }

      const column = this.gridColumnApi.getColumn(columnId);
      const headerName = (column?.getColDef?.()?.headerName || '').toString().trim();
      return !!headerName;
    };

    const navigableCells = this.skipLeadingEmptyHeaderOnTab
      ? headerCells.filter((cell) => hasMeaningfulHeader(cell))
      : headerCells;

    navigableCells.forEach((cell) => cell.setAttribute('tabindex', '0'));
    headerCells
      .filter((cell) => !navigableCells.includes(cell))
      .forEach((cell) => cell.setAttribute('tabindex', '-1'));

    return navigableCells;
  }

  private getFirstNavigableHeaderCell(): HTMLElement | null {
    return this.getNavigableHeaderCells()[0] || null;
  }

  private getLastNavigableHeaderCell(): HTMLElement | null {
    const headerCells = this.getNavigableHeaderCells();
    return headerCells.length ? headerCells[headerCells.length - 1] : null;
  }

  private getLastNavigableHeaderColumn(): any | null {
    const lastHeaderCell = this.getLastNavigableHeaderCell();
    const columnId = lastHeaderCell?.getAttribute('col-id');
    if (!columnId || !this.gridColumnApi) {
      return null;
    }

    return this.gridColumnApi.getColumn(columnId) || null;
  }

  onAllChecked(isChecked) {
    for (let i = 0; i < this.allGridData.length; i++) {
      if (!this.searchText && !this.filters) {
        if (!this.allGridData[i].isEditDisabled && !this.allGridData[i].isCheckboxDisabled) {
          this.allGridData[i].selected = isChecked;
        }
      }
      if (this.gridData.length > i) {
        if (!this.gridData[i].isEditDisabled && !this.gridData[i].isCheckboxDisabled) {
          this.gridData[i].selected = isChecked;
        }
      }
    }
    this.isAllChecked = isChecked;
    this.checkUncheckAll();
    this.onCheckAll.emit(isChecked);
  }

  refreshData(options?: { preserveScroll?: boolean }) {
    this.sharedValue.isGridDataLoading = false;
    if (this.gridApi) {
      this.isDataLoading = false;
      this.sharedValue.isGridDataLoading = false;
      if (this.gridlastRowIndex > 0) {
        // Capture the current scroll position of the ag-grid body viewport
        // so we can restore it after `refreshInfiniteCache()` re-renders the
        // visible blocks (which otherwise causes a visible jump when
        // load-more appends rows).
        const viewport = options?.preserveScroll
          ? (this.agGridElement?.nativeElement as HTMLElement | undefined)
              ?.querySelector('.ag-body-viewport') as HTMLElement | null
          : null;
        const savedScrollTop = viewport ? viewport.scrollTop : 0;

        if (viewport && savedScrollTop > 0) {
          // ag-grid re-creates rendered rows asynchronously after
          // `refreshInfiniteCache()`. Until the new rows are in the DOM the
          // viewport's `scrollHeight` is transiently small, so any
          // `scrollTop` we set is clamped to 0 by the browser — that is what
          // causes the "jump to first row".
          //
          // Hook `modelUpdated` (fires after ag-grid has rebuilt the visible
          // rows) and restore `scrollTop` only once `scrollHeight` is at
          // least the value we saved. We try a few model updates because the
          // first one can arrive before all blocks have rendered.
          let attempts = 0;
          const restore = () => {
            if (viewport.scrollHeight >= savedScrollTop + viewport.clientHeight) {
              viewport.scrollTop = savedScrollTop;
              this.gridApi.removeEventListener('modelUpdated', restore);
              return;
            }
            // Fallback: assign anyway after a few attempts so we don't leak
            // the listener if the grid never grows tall enough.
            if (++attempts >= 5) {
              viewport.scrollTop = savedScrollTop;
              this.gridApi.removeEventListener('modelUpdated', restore);
            }
          };
          this.gridApi.addEventListener('modelUpdated', restore);
        }

        this.gridApi.refreshInfiniteCache();
      }
      else {
        this.resetDataSource();
      }
    }
  }

  /**
   * Used by `fnAppendAllDataInGrid` for the infinite-scroll load-more flow.
   *
   * Unlike `refreshData()` which calls `refreshInfiniteCache()` (invalidating
   * every cached block and forcing a re-render of all currently-visible rows
   * — which causes the brief "dark flash" users see on grids with custom
   * cell renderers), this method only updates ag-grid's known row count so
   * the new rows become reachable. Existing rendered rows are left in place,
   * preserving both the user's scroll position and the cells they were
   * looking at.
   */
  private extendInfiniteRowCount() {
    if (!this.gridApi) return;
    this.gridlastRowIndex = this.searchText && !this.isServerSideSearchMode()
      ? (this.gridData?.length ?? 0)
      : this.allGridData.length;

    // ag-grid v31 exposes `setRowCount(count, lastRowKnown)` on the grid
    // API for the infinite row model. Pass `true` because we already know
    // the new total — the next page (if any) will extend it again.
    if (typeof (this.gridApi as any).setRowCount === 'function') {
      (this.gridApi as any).setRowCount(this.gridlastRowIndex, true);
    } else if (typeof (this.gridApi as any).setInfiniteRowCount === 'function') {
      // Older ag-grid versions
      (this.gridApi as any).setInfiniteRowCount(this.gridlastRowIndex, true);
    } else {
      // Last-resort fallback: behave like before so we don't break the
      // grid on unexpected ag-grid versions.
      this.refreshData({ preserveScroll: true });
    }
  }

  onGridFocusOut(event: FocusEvent) {
    const nextTarget = event.relatedTarget as Node | null;

    if (nextTarget && this.agGridElement?.nativeElement?.contains(nextTarget)) {
      return;
    }

    requestAnimationFrame(() => {
      if (this.isGridFocusWithin()) {
        return;
      }

      this.isKeyboardFocusVisible = false;
    });
  }

  onGridPointerInteraction() {
    AppGridComponent.isKeyboardNavigation = false;
    this.isKeyboardFocusVisible = false;
  }

  private isGridFocusWithin(): boolean {
    const activeElement = document.activeElement as Node | null;
    return !!activeElement && !!this.agGridElement?.nativeElement?.contains(activeElement);
  }

  resetDataSource() {
    this.gridApi.setDatasource(this.getDataSource());
  }

  public resetScrollToTop(): void {
    if (this.isComponentDestroyed) {
      return;
    }

    const gridRoot = this.agGridElement?.nativeElement;
    if (!gridRoot) {
      return;
    }

    this.clearPendingActionFocusForThisGrid();
    this.clearStoredCellFocus();

    const scrollContainers = gridRoot.querySelectorAll<HTMLElement>(
      '.ag-body-viewport, .ag-center-cols-viewport, .ag-body-vertical-scroll-viewport'
    );
    scrollContainers.forEach(container => container.scrollTop = 0);

    if (this.gridApi && !this.isGridDestroyed()) {
      this.gridApi.ensureIndexVisible(0, 'top');
    }
  }

  private scheduleScrollReset(): void {
    clearTimeout(this.scrollResetTimeout);
    this.scrollResetTimeout = setTimeout(() => {
      this.scrollResetTimeout = undefined;
      requestAnimationFrame(() => this.resetScrollToTop());
    }, 0);
  }

  onDataLoadStart() {
    this.gridApi.showLoadingOverlay();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.updateGridFocusKey();
    this.resetSortModel();

    this.gridApi.setDatasource(this.getDataSource());
    this.gridApi.addEventListener('modelUpdated', this.onGridModelUpdated);
    if (this.resetScrollOnVisible) {
      this.scheduleScrollReset();
    }
    setTimeout(() => this.handleReturnFocusRestore(true), 0);
  }

  private isGridDestroyed(): boolean {
    return !!(this.gridApi as any)?.isDestroyed?.();
  }

  private releaseGridFocus(): void {
    const activeElement = document.activeElement as HTMLElement | null;
    if (activeElement && this.agGridElement?.nativeElement?.contains(activeElement) && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
  }

  private clearPendingActionFocusForThisGrid(): void {
    const pendingFocus = GridActionFocusRestore.get();
    if (pendingFocus && this.isPendingActionFocusForThisGrid(pendingFocus)) {
      GridActionFocusRestore.clear();
    }
  }

  private debounceScroll(event?:any) {
    if (this.isComponentDestroyed) {
      return;
    }

    clearTimeout(this.debounceTimeout);

    // Only consider vertical scrolls when ag-grid tells us the direction.
    // Some scroll events (programmatic, first event after a refresh, or
    // horizontal-scroll wheel events on touchpads) arrive without a
    // `direction` property; treat those as vertical too so we don't miss
    // bottom-hits.
    if (event && event.direction === 'horizontal') return;

    if (!this.gridApi || this.allGridData.length === 0) return;

    // Pixel-based bottom detection — robust across ag-grid row models and
    // does not depend on `getLastDisplayedRowIndex()` which can be -1 in
    // infinite mode before all blocks have loaded.
    const range = this.gridApi.getVerticalPixelRange();
    if (!range) return;

    const rowHeight = this.gridOptions.rowHeight || 25;
    const totalContentHeight = this.allGridData.length * rowHeight;
    // Trigger when within one row of the bottom of the data.
    const nearBottom = range.bottom >= totalContentHeight - rowHeight;

    if (nearBottom) {
      this.debounceTimeout = setTimeout(() => this.onScrollEnd(), 200);
    }
  }

  private onScrollEnd() {
  // `onLoadMore` is intentionally NOT guarded here: some pages don't supply
  // it and we still want this to be a no-op rather than blocking future
  // calls. Guard only against missing grid, exhausted data and in-flight
  // requests.
  if (!this.gridApi || !this.hasMoreItems || this.isLoadingMore) return;
  if (!this.onLoadMore) return;

  // Re-verify the user really is at the bottom (handles cases where the
  // grid scrolled away between the debounced callback being scheduled and
  // firing).
  const range = this.gridApi.getVerticalPixelRange();
  if (!range) return;
  const rowHeight = this.gridOptions.rowHeight || 25;
  const totalContentHeight = this.allGridData.length * rowHeight;

  if (range.bottom >= totalContentHeight - rowHeight) {
    this.loadNextPage(true);
   }
  }  

  private loadNextPage(restoreScrollAnchor: boolean = false): void {
    if (!this.gridApi || !this.onLoadMore || !this.hasMoreItems || this.isLoadingMore) {
      return;
    }

    this.isLoadingMore = true;
    this.currentOffset = this.currentOffset + this.loadMorePageSize;
    const lastNode = this.gridApi.getRenderedNodes().slice(-1)[0];
    const lastIndex = lastNode?.rowIndex ?? 0;
    const bodyViewport = this.agGridElement?.nativeElement?.querySelector('.ag-body-viewport') as HTMLElement | null;
    const previousScrollTop = bodyViewport?.scrollTop ?? 0;

    // Safety net: if the consumer never responds (e.g. API error swallowed
    // by the page) clear the in-flight flag so the user can scroll again.
    // Most callers ignore the callback we pass in and instead reset the
    // flag via fnAppendAllDataInGrid / fnReplaceAllDataInGrid, so this is
    // only a last-resort fallback.
    const safetyTimeout = setTimeout(() => {
      this.isLoadingMore = false;
    }, 10000);

    try {
      this.onLoadMore(this.currentOffset, this.searchText, () => {
        clearTimeout(safetyTimeout);
        if (restoreScrollAnchor) {
          if (this.shouldAutoFillViewport()) {
            const updatedBodyViewport = this.agGridElement?.nativeElement?.querySelector('.ag-body-viewport') as HTMLElement | null;
            if (updatedBodyViewport) {
              updatedBodyViewport.scrollTop = previousScrollTop;
            }
          } else {
            this.gridApi.ensureIndexVisible(lastIndex, 'top');
          }
        }
      });
    } catch (e) {
      // Don't let a consumer exception leave the grid stuck in 'loading'.
      clearTimeout(safetyTimeout);
      this.isLoadingMore = false;
      throw e;
    }
  }

  private getDataSource(): IDatasource {
    return {
      getRows: (params: IGetRowsParams) => {
        this.getRowData(params).subscribe(data => {
          this.gridlastRowIndex = 0;
          if (this.searchText && !this.isServerSideSearchMode()) {
            this.gridlastRowIndex = this.gridData.length;
          }
          else {
            this.gridlastRowIndex = this.allGridData.length;
          }
          this.isDataLoading = false;
          if (this.gridlastRowIndex > 0) {           
            this.gridApi.hideOverlay();
          }
          else if (this.isDataLoading) {
            this.gridApi.showLoadingOverlay();
          }
          else {
            this.gridApi.showNoRowsOverlay();
          }
          params.successCallback(data, this.gridlastRowIndex);
            this.scheduleViewportFillCheck();
        });
      }
    };
  }

    private scheduleViewportFillCheck(): void {
      if (!this.shouldAutoFillViewport()) {
        return;
      }

      if (this.viewportFillTimeout) {
        clearTimeout(this.viewportFillTimeout);
      }

      this.viewportFillTimeout = setTimeout(() => {
        this.viewportFillTimeout = undefined;
        this.ensureViewportCanScroll();
      }, 50);
    }

    private ensureViewportCanScroll(): void {
      if (!this.shouldAutoFillViewport()) {
        return;
      }

      if (!this.gridApi || !this.onLoadMore || !this.hasMoreItems || this.isLoadingMore || !this.allGridData?.length) {
        return;
      }

      if (this.hasViewportOverflow()) {
        return;
      }

      this.loadNextPage(false);
    }

    private shouldAutoFillViewport(): boolean {
      return this.viewportAutoFillParents.has(this.parentComponent);
    }

    private hasViewportOverflow(): boolean {
      const gridApiAny = this.gridApi as any;
      if (typeof gridApiAny?.isVerticalScrollShowing === 'function') {
        return !!gridApiAny.isVerticalScrollShowing();
      }

      const gridRoot = this.agGridElement?.nativeElement;
      if (!gridRoot) {
        return false;
      }

      const verticalScrollViewport = gridRoot.querySelector('.ag-body-vertical-scroll-viewport') as HTMLElement | null;
      if (verticalScrollViewport && verticalScrollViewport.scrollHeight > verticalScrollViewport.clientHeight + 1) {
        return true;
      }

      const verticalScroll = gridRoot.querySelector('.ag-body-vertical-scroll') as HTMLElement | null;
      if (verticalScroll && verticalScroll.clientHeight > 0 && verticalScroll.offsetWidth > 0) {
        return true;
      }

      const centerViewport = gridRoot.querySelector('.ag-center-cols-viewport') as HTMLElement | null;
      if (centerViewport && centerViewport.scrollHeight > centerViewport.clientHeight + 1) {
        return true;
      }

      const bodyViewport = gridRoot.querySelector('.ag-body-viewport') as HTMLElement | null;
      return !!bodyViewport && bodyViewport.scrollHeight > bodyViewport.clientHeight + 1;
    }

  private getRowData(params: IGetRowsParams) {
    this.sortAllRows(params);
    this.gridData = [];
    if (this.searchText && !this.isServerSideSearchMode()) {
      this.gridData = this.filterRows();
    }
    else {
      this.gridData = this.allGridData;
    }
    const rowsForBlock = this.gridData.slice(params.startRow, params.endRow);
    return of(rowsForBlock);
  }

  private isServerSideSearchMode(): boolean {
    return !!this.parentComponent && this.parentComponent !== 'usermanagement';
  }

  private filterRows() {
  const rows = [];
  const search = this.searchText?.toLowerCase() ?? '';

  for (let i = 0; i < this.allGridData.length; i++) {
    let isRowVisible = false;

    for (let j = 0; j < this.columnDefs.length; j++) {
      const key = this.columnDefs[j].field;
      const cellValue = this.allGridData[i][key];

      // ✅ Normal search
      if (cellValue && search && cellValue.toString().toLowerCase().includes(search)) {
        isRowVisible = true;
        break;
      }

      // ✅ Extra condition for sales_organization_count (only in usermanagement)
      if (this.parentComponent === 'usermanagement' && key === 'sales_organization_count' && search) {
        const userSalesOrgs = this.allGridData[i].UserSalesOrgAssociation || [];
        const orgMatch = userSalesOrgs.some(org =>
          (org.SalesOrgName && org.SalesOrgName.toLowerCase().includes(search)) ||
          (org.SalesOrgId && org.SalesOrgId.toLowerCase().includes(search))
        );
        if (orgMatch) {
          isRowVisible = true;
          break;
        }
      }

      // ✅ Extra condition for User_Roles
      if (key === 'User_Roles' && search) {
        const userRoles = this.allGridData[i].User_Roles || [];
        const roleMatch = userRoles.some(role =>
          role && role.toLowerCase().includes(search)
        );
        if (roleMatch) {
          isRowVisible = true;
          break;
        }
      }
    }

    // ✅ Push visible rows into result
    if (isRowVisible) {
      rows.push(this.allGridData[i]);
    }
  }

  return rows;
}


  private sortAllRows(params) {
    if (params.sortModel && params.sortModel.length > 0) {
      if (this.gridSortColumn != params.sortModel[0].colId
        || this.gridSortOrder != params.sortModel[0].sort
        || this.isRefresh) {
        params.startRow = 0;
        params.endRow = this.gridOptions.cacheBlockSize;
        this.gridSortColumn = params.sortModel[0].colId;
        this.gridSortOrder = params.sortModel[0].sort;
        this.allGridData = this.sortByKey(this.allGridData, this.gridSortColumn, this.gridSortOrder);
      }
    }
    else {
      if (this.gridConfigItems.defaultSortColumn && (!this.gridSortColumn || !this.gridSortOrder || this.isRefresh)) {
        params.startRow = 0;
        params.endRow = this.gridOptions.cacheBlockSize;
        this.gridSortColumn = this.gridConfigItems.defaultSortColumn;
        this.gridSortOrder = this.gridConfigItems.defaultSortOrder;
        this.allGridData = this.sortByKey(this.allGridData, this.gridSortColumn, this.gridSortOrder);
      }
    }
    this.isRefresh = false;
  }

/**
 * Parses ddmmmyyyy date string to Date object
 * 
 * @param dateStr 
 * @returns ddmmmyyyy 
 */
private parseDDMMMYYYY(dateStr: string): Date | null {
  if (!dateStr) return null;
  // Try parsing as ISO date string first (e.g., "2025-07-13T10:30:00")
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) return isoDate;
  // Fallback: parse DD-MMM-YYYY format (e.g., "13-Jul-2025")
  const months = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
  };
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = months[parts[1]];
  const year = parseInt(parts[2], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  return new Date(year, month, day);
}

/**
 * Sorts by key in ascending or descending order
 * 
 * @param array 
 * @param key 
 * @param order 
 * @returns sorted array
 */
private sortByKey(array: any[], key: string, order: string) {
  const dir = (typeof order === 'string' && order.toLowerCase() === 'asc') ? 'asc' : 'desc';
 
  // normalize to only "asc" or "desc"

  const dateColumns = ['created_at'];
  const isDateColumn = dateColumns.includes(key);

  const copy = [...array];
  const hasSoftDeletedRows = copy.some(item => Object.prototype.hasOwnProperty.call(item || {}, 'isSoftDeleted'));

  return copy.sort((a, b) => {
    if (hasSoftDeletedRows) {
      const aSoftDeleted = Boolean(a?.isSoftDeleted);
      const bSoftDeleted = Boolean(b?.isSoftDeleted);

      if (aSoftDeleted !== bSoftDeleted) {
        return aSoftDeleted ? 1 : -1;
      }
    }

    let x = a[key];
    let y = b[key];

    if (isDateColumn) {
      x = this.parseDDMMMYYYY(x);
      y = this.parseDDMMMYYYY(y);
    }

    if (typeof x === 'string') x = x.toLowerCase();
    if (typeof y === 'string') y = y.toLowerCase();

    if (x == null && y == null) return 0;
    if (x == null) return dir === 'asc' ? -1 : 1;
    if (y == null) return dir === 'asc' ? 1 : -1;

    if (x < y) return dir === 'asc' ? -1 : 1;
    if (x > y) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}



  private autosizeHeaders(event) {
    if (event.finished !== false) {
      event.api.setHeaderHeight(this.header_MIN_HEIGHT);
      const headerCells = document.querySelectorAll('#myGrid .ag-header-cell-label');
      let minHeight = this.header_MIN_HEIGHT;
      headerCells.forEach(cell => {
        minHeight = Math.max(minHeight, cell.scrollHeight);
      });
      event.api.setHeaderHeight(minHeight);
      event.api.setGroupHeaderHeight(this.group_MIN_HEIGHT);
    }
  }

  public refreshGridCells(columnList: string[]) {
    if (columnList) {
      this.gridApi.refreshCells({
        columns: columnList,
        force: true,
        suppressFlash: true
      });
    }
  }

  public redrawGridRows() {
    this.gridApi.redrawRows();
  }

 public onButtonClick() {
    if (this.gridConfigItems?.isButtonDisabled) {
      return;
    }
    this.onBtnClick.emit();
  }

  exportData() {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(this.gridConfigItems.sheetName);
    worksheet.columns = [];
    const sheetColumns = [];
    this.columnDefs.forEach(element => {
      if(element.field !='userActionField')
        {sheetColumns.push({ header: element.headerName, key: element.field, width: 30 });}
    });
    worksheet.columns = sheetColumns;

    const data  = this.gridData;
      // Add rows to worksheet
    worksheet.addRows(data , 'n');

    
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      fs.saveAs(blob, this.gridConfigItems.workbookName);
    });
  }

}


